/**
 * Theme-aware badge color references for status-style badges (project stages,
 * stage-bearing signals, and any other badge that wants a tuned tone instead of a
 * stock variant). The canonical tone names and light literals live in
 * @parcel/types; this module maps each tone to its theme CSS variables.
 *
 * Each tone is a "soft" badge — a light background, a light border, a mid-strength
 * dot/accent, and dark text. The set is organized as phase pairs: a light
 * ("entering") and dark ("completing") tone per hue, plus a standalone amber.
 *
 * The `dot` value doubles as the standalone accent color for non-badge surfaces
 * (e.g. map markers), so a stage's marker matches its badge.
 *
 * Values are CSS variables (defined in styles/globals.css, `:root` + `.dark`)
 * rather than literal hex, so a tone resolves light or dark by theme with no JS.
 * The light `:root` values are synchronized with the shared literal palette by a
 * test; the `.dark` block is the dark sister palette. Consumers that apply these
 * via inline style or DOM (Badge, map markers) resolve the var against the document
 * automatically.
 */
import type { BadgeToneName } from '@parcel/types';

export interface BadgeToneColors {
  /** Badge background fill. */
  bg: string;
  /** Badge border. */
  border: string;
  /** Leading dot / accent color (also used as the standalone marker color). */
  dot: string;
  /** Badge text/foreground. */
  fg: string;
}

export const BADGE_TONES = {
  'gray-light': {
    bg: 'var(--tone-gray-light-bg)',
    border: 'var(--tone-gray-light-border)',
    dot: 'var(--tone-gray-light-dot)',
    fg: 'var(--tone-gray-light-fg)',
  },
  'gray-dark': {
    bg: 'var(--tone-gray-dark-bg)',
    border: 'var(--tone-gray-dark-border)',
    dot: 'var(--tone-gray-dark-dot)',
    fg: 'var(--tone-gray-dark-fg)',
  },
  'blue-light': {
    bg: 'var(--tone-blue-light-bg)',
    border: 'var(--tone-blue-light-border)',
    dot: 'var(--tone-blue-light-dot)',
    fg: 'var(--tone-blue-light-fg)',
  },
  'blue-dark': {
    bg: 'var(--tone-blue-dark-bg)',
    border: 'var(--tone-blue-dark-border)',
    dot: 'var(--tone-blue-dark-dot)',
    fg: 'var(--tone-blue-dark-fg)',
  },
  'green-light': {
    bg: 'var(--tone-green-light-bg)',
    border: 'var(--tone-green-light-border)',
    dot: 'var(--tone-green-light-dot)',
    fg: 'var(--tone-green-light-fg)',
  },
  'green-dark': {
    bg: 'var(--tone-green-dark-bg)',
    border: 'var(--tone-green-dark-border)',
    dot: 'var(--tone-green-dark-dot)',
    fg: 'var(--tone-green-dark-fg)',
  },
  amber: {
    bg: 'var(--tone-amber-bg)',
    border: 'var(--tone-amber-border)',
    dot: 'var(--tone-amber-dot)',
    fg: 'var(--tone-amber-fg)',
  },
} as const satisfies Record<BadgeToneName, BadgeToneColors>;

export type BadgeTone = BadgeToneName;
